/**
 * Domain-based configuration for multi-domain routing
 */

export interface DomainConfig {
  name: string;
  type: 'commercial' | 'opensource';
  allowedPaths: string[];
  blockedPaths: string[];
  theme?: 'corporate' | 'community' | 'product';
  features?: string[];
}

export interface NavigationItem {
  path: string;
  [key: string]: unknown;
}

// Get current domain
export const getCurrentDomain = (): string => {
  if (typeof window === 'undefined') return 'localhost';
  let host = window.location.hostname;
  // A `staging.<brand>` host resolves to the <brand> configuration, so the staging
  // review site renders the real brand (full clean site). The go-quiet holding page
  // is gated separately in isPublicQuietMode() and never applies to staging.
  if (host.startsWith('staging.')) host = host.slice(8);

  // Staging/preview override: on NON-production hosts only, ?domain=<host>
  // previews a specific site (e.g. ?domain=qsave.org). Persists in localStorage;
  // ?domain=clear resets. Ignored on real production domains so it cannot be
  // used to bypass the brand split in production.
  const PRODUCTION_HOSTS = ['qkey.co', 'pqp.to', 'opo.it.com', 'qsave.org', 'nwf.so', 'qday.is'];
  const isProductionHost = PRODUCTION_HOSTS.some((h) => host === h || host === `www.${h}`);
  if (!isProductionHost) {
    try {
      const q = new URLSearchParams(window.location.search).get('domain');
      if (q === 'clear' || q === 'reset' || q === 'none') {
        localStorage.removeItem('qsave-domain-override');
      } else if (q) {
        localStorage.setItem('qsave-domain-override', q);
        return q;
      }
      const stored = localStorage.getItem('qsave-domain-override');
      if (stored) return stored;
    } catch {
      /* localStorage unavailable — fall through to hostname */
    }
  }
  return host;
};

// True when served from a `staging.<brand>` host (attorney/internal review site).
export const isStagingHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.startsWith('staging.');
};

// "Go quiet" public holding-page gate. Enabled by env var VITE_PUBLIC_QUIET === 'true'.
// Never applies to staging hosts or localhost — so review and dev always see the full site.
export const isPublicQuietMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (isStagingHost()) return false;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.')) return false;
  return import.meta.env.VITE_PUBLIC_QUIET === 'true';
};

// Domain configurations
export const DOMAIN_CONFIGS: Record<string, DomainConfig> = {
  // Commercial domains
  'qkey.co': {
    name: 'QKey Corporate',
    type: 'commercial',
    allowedPaths: [
      '/',
      '/about',
      '/quantum-vault-pqp',
      '/hd-wallet',
      '/quantum-keys',
      '/pqp',
      '/pq-exchange',
      '/uspq',
      '/pq-chain',
      '/protection-workflow',
      '/wallets',
      '/enterprise',
      '/contact',
      '/standards-mark',
      '/brand-guidelines'
    ],
    blockedPaths: [
      // Whitepaper held back from public release (per current decision).
      // Block BEFORE any allow so /whitepapers redirects away on qkey.co too.
      '/whitepapers', '/whitepaper',
      // QKey commercial site — hide QSAVE protocol/token/fund pages.
      // (Brand split; confirm/adjust against the parked QSAVE/QKey decisions.)
      '/token', '/tokenomics', '/non-sovereign-wealth-fund', '/philanthropic-foundation', '/qsave',
      '/recovery', '/qd3-registration'
    ],
    theme: 'corporate',
    features: ['vault', 'pqp', 'enterprise']
  },
  
  'pqp.to': {
    name: 'Post-Quantum Pay',
    type: 'commercial',
    allowedPaths: [
      '/',
      '/pqp',
      '/docs',
      '/integration',
      '/demo',
      '/pricing',
      '/protection-workflow'
    ],
    blockedPaths: ['/token', '/tokenomics', '/non-sovereign-wealth-fund', '/philanthropic-foundation', '/governance'],
    theme: 'product',
    features: ['pqp']
  },
  
  // Open source domains
  'qsave.org': {
    name: 'QSAVE Project',
    type: 'opensource',
    allowedPaths: [
      '/',
      '/about',
      '/token',
      '/tokenomics',
      '/non-sovereign-wealth-fund',
      '/philanthropic-foundation',
      '/qd3-registration',
      '/recovery',
      '/automatic-migration',
      '/register',
      '/implementers',
      '/wallet-hardening',
      '/standards-mark',
      '/contact',
      '/faq'
    ],
    blockedPaths: [
      // Whitepaper held back from public release (per current decision).
      // Block BEFORE any allow so /whitepapers redirects away on qsave.org.
      '/whitepapers', '/whitepaper',
      // Standards Mark: reachable by direct URL (in allowedPaths) but intentionally
      // NOT in the nav — it is a proposed standard still going through registration
      // and has no independent home yet. It also remains live on qkey.co.
      // QSAVE protocol site — hide QKey commercial product pages.
      // (Brand split; confirm/adjust against the parked QSAVE/QKey decisions.)
      '/uspq', '/qusd', '/pq-exchange', '/quantum-vault-pqp', '/pq-chain',
      '/pqp', '/hd-wallet', '/quantum-keys', '/wallets', '/protection-workflow',
      '/partners'
    ],
    theme: 'community',
    features: ['token', 'governance', 'qd3']
  },
  
  'qday.is': {
    name: 'Quantum Day Response',
    type: 'opensource',
    allowedPaths: [
      '/',
      '/status',
      '/check',
      '/migrate',
      '/resources',
      '/alert',
      '/bitcoin-tools'
    ],
    blockedPaths: ['/quantum-vault', '/pqp', '/token/sale'],
    theme: 'community',
    features: ['emergency', 'education']
  },
  
  // Development
  'localhost': {
    name: 'Development',
    type: 'commercial',
    allowedPaths: ['*'], // Allow all paths in development
    blockedPaths: [],
    features: ['*']
  }
};

// Get config for current domain
export const getDomainConfig = (): DomainConfig => {
  const domain = getCurrentDomain();
  
  // Check exact match first
  if (DOMAIN_CONFIGS[domain]) {
    return DOMAIN_CONFIGS[domain];
  }
  
  // Check if it's a subdomain (e.g., www.qkey.co)
  const baseDomain = domain.replace(/^www\./, '');
  if (DOMAIN_CONFIGS[baseDomain]) {
    return DOMAIN_CONFIGS[baseDomain];
  }
  
  // Check partial matches for development
  for (const [key, config] of Object.entries(DOMAIN_CONFIGS)) {
    if (domain.includes(key)) {
      return config;
    }
  }
  
  // Default to localhost config
  return DOMAIN_CONFIGS.localhost;
};

// Check if a path is allowed for current domain
export const isPathAllowed = (path: string): boolean => {
  const config = getDomainConfig();
  
  // Development allows everything
  if (config.allowedPaths.includes('*')) return true;
  
  // Check if explicitly blocked
  if (config.blockedPaths.some(blocked => path.startsWith(blocked))) {
    return false;
  }
  
  // Check if explicitly allowed
  if (config.allowedPaths.some(allowed => path.startsWith(allowed))) {
    return true;
  }
  
  // Default to blocked for production domains
  return config.name === 'Development';
};

// Get filtered navigation items based on domain
export const getNavigationItems = <T extends NavigationItem>(allItems: T[]): T[] => {
  return allItems.filter(item => {
    // Always show public pages
    if (['/about', '/faq', '/terms', '/privacy'].includes(item.path)) {
      return true;
    }
    
    // Filter based on domain config
    return isPathAllowed(item.path);
  });
};

// Check if current domain should show token interaction warning
export const shouldShowTokenWarning = (): boolean => {
  const config = getDomainConfig();
  return config.type === 'opensource' && (config.features?.includes('token') || false);
};

// Get domain-specific theme class
export const getDomainThemeClass = (): string => {
  const config = getDomainConfig();
  switch (config.theme) {
    case 'corporate':
      return 'theme-corporate';
    case 'community':
      return 'theme-community';
    case 'product':
      return 'theme-product';
    default:
      return 'theme-default';
  }
};

// Per-brand <title> / favicon / meta description for the multi-domain app.
// index.html ships QKey defaults (plus an inline hostname-based override for the
// no-flash case); this is the canonical source and additionally honours the
// ?domain preview override on non-production hosts via getCurrentDomain().
export interface BrandMeta {
  title: string;
  favicon: string;
  description: string;
}

export const getBrandMeta = (): BrandMeta => {
  const domain = getCurrentDomain().replace(/^www\./, '');
  const QSAVE: BrandMeta = {
    title: "QSAVE don't Burn",
    favicon: '/favicon-qsave.png',
    description:
      "QSAVE — Quantum Secure Asset Verification & Endowment. A quantum migration solution for the 'Satoshi's Coins' problem that builds a Charitable Endowment for the good of all.",
  };
  const QKEY: BrandMeta = {
    title: 'QKey — Post-Quantum Bitcoin',
    favicon: '/favicon.png',
    description:
      'QKey — the factory for quantum-safe Bitcoin: hardened post-quantum wallets, two-factor spend protection (PQP), and tooling that works with Bitcoin as it exists today.',
  };
  const RETIRED: BrandMeta = {
    title: 'Reserved for future use',
    favicon: '/favicon.png',
    description: 'This domain is reserved for future use.',
  };
  switch (domain) {
    case 'qsave.org':
      return QSAVE;
    case 'opo.it.com':
    case 'nwf.so':
      return RETIRED;
    case 'pqp.to':
      return {
        title: 'PQP — Post-Quantum Pay',
        favicon: '/favicon.png',
        description:
          'Post-Quantum Pay (PQP) — two-factor spend protection that secures Bitcoin against quantum key recovery, working with Bitcoin as it exists today.',
      };
    case 'qday.is':
      return {
        title: 'Q-Day — Quantum Threat Monitor',
        favicon: '/favicon.png',
        description:
          'Q-Day — tracking the quantum threat to Bitcoin and how to prepare before the cryptography that secures it can be broken.',
      };
    default:
      // qkey.co, opo.it.com, localhost
      return QKEY;
  }
};
